import type { Address, Hours, Location } from "./types";

export const SERVICE_LABELS: Record<string, string> = {
  send_money: "Send money",
  receive_money: "Receive money",
  money_order: "Money order",
  bill_pay: "Bill pay",
  mobile_topup: "Mobile top-up",
  crypto_buy: "Buy crypto",
  crypto_sell: "Sell crypto",
  debit_card: "Debit card",
  wallet_cashin: "Wallet cash-in",
  wallet_cashout: "Wallet cash-out",
  is_money_order_only: "Money orders only",
};

export const SERVICE_ICONS: Record<string, string> = {
  send_money: "↗",
  receive_money: "↙",
  money_order: "✉",
  bill_pay: "🧾",
  mobile_topup: "📱",
  crypto_buy: "₿",
  crypto_sell: "₿",
  debit_card: "💳",
  wallet_cashin: "👛",
  wallet_cashout: "👛",
};

export function formatAddress(a?: Address): string {
  if (!a) return "";
  return [a.line1, a.line2, a.city, a.state_province, a.postal_code, a.country]
    .filter(Boolean)
    .join(", ");
}

export interface StatusBadge {
  label: string;
  cls: string;
}

const HOURS_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

/** Build the open/closed badge for a location. Mirrors the demo logic. */
export function statusBadge(loc: Location): StatusBadge {
  const h = loc.hours;
  if (!h) {
    return {
      label: "Hours unavailable",
      cls: "bg-slate-100 text-slate-600 border-slate-200",
    };
  }
  const now = new Date();
  let dayKey: (typeof HOURS_KEYS)[number] = HOURS_KEYS[now.getDay()];
  let cur = now.getHours() * 60 + now.getMinutes();
  if (h.tz) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: h.tz,
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(now);
      const weekday = parts.find((p) => p.type === "weekday")?.value.toLowerCase();
      const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "");
      const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "");
      if (weekday && HOURS_KEYS.includes(weekday as (typeof HOURS_KEYS)[number])) {
        dayKey = weekday as (typeof HOURS_KEYS)[number];
      }
      if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
        cur = hour * 60 + minute;
      }
    } catch {
      // Fall back to local time if the timezone string is invalid.
    }
  }
  const day = (h as Hours)[dayKey];
  if (!day || day.length < 2) {
    return {
      label: "Closed today",
      cls: "bg-rose-50 text-rose-700 border-rose-200",
    };
  }
  const [open, close] = day;
  const toMin = (s: string) => {
    const [hh, mm] = s.split(":").map((n) => parseInt(n, 10));
    return hh * 60 + (mm || 0);
  };
  if (cur >= toMin(open) && cur < toMin(close)) {
    return {
      label: `Open · closes ${close}`,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
  }
  return {
    label: `Closed · opens ${open}`,
    cls: "bg-rose-50 text-rose-700 border-rose-200",
  };
}

const BRAND_COLORS: Record<string, string> = {
  walmart: "#0071CE",
  cvs: "#CC0000",
  ace_cash_express: "#22C55E",
  bobs_market: "#F59E0B",
  post_office: "#1F2937",
  oxxo: "#EF4444",
  seven_eleven: "#16A34A",
  rite_aid: "#1E40AF",
  moneygram: "#DA291C",
};

export function brandColor(loc: Location): string {
  const slug = loc.partner_chain?.slug ?? "moneygram";
  return BRAND_COLORS[slug] ?? "#DA291C";
}

export function brandInitial(loc: Location): string {
  const name = loc.partner_chain?.display_name ?? loc.name ?? "M";
  return name[0]?.toUpperCase() ?? "M";
}
